import React from 'react';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';

const isYoutubeSource = (url: string) => {
  if (!url) return false;
  return /youtu\.be\//.test(url) ||
    /youtube\.com\/(watch\?v=|embed\/)/.test(url) ||
    /youtube\.com\/shorts\//.test(url);
};

const toYoutubeEmbedUrl = (url: string) => {
  if (!url) return '';
  const shortMatch = url.match(/youtu\.be\/([^?&#]+)/);
  if (shortMatch) {
    return `https://www.youtube.com/embed/${shortMatch[1]}`;
  }
  const shortsMatch = url.match(/youtube\.com\/shorts\/([^?&#]+)/);
  if (shortsMatch) {
    return `https://www.youtube.com/embed/${shortsMatch[1]}`;
  }
  const watchMatch = url.match(/[?&]v=([^?&#]+)/);
  if (watchMatch) {
    return `https://www.youtube.com/embed/${watchMatch[1]}`;
  }
  const embedMatch = url.match(/youtube\.com\/embed\/([^?&#]+)/);
  if (embedMatch) {
    return `https://www.youtube.com/embed/${embedMatch[1]}`;
  }
  return url;
};

const getYoutubeVideoId = (url: string) => {
  if (!url) return '';
  const shortMatch = url.match(/youtu\.be\/([^?&#]+)/);
  if (shortMatch) {
    return shortMatch[1];
  }
  const shortsMatch = url.match(/youtube\.com\/shorts\/([^?&#]+)/);
  if (shortsMatch) {
    return shortsMatch[1];
  }
  const watchMatch = url.match(/[?&]v=([^?&#]+)/);
  if (watchMatch) {
    return watchMatch[1];
  }
  const embedMatch = url.match(/youtube\.com\/embed\/([^?&#]+)/);
  if (embedMatch) {
    return embedMatch[1];
  }
  return '';
};

const getYoutubeThumbnailUrl = (url: string) => {
  const id = getYoutubeVideoId(url);
  if (!id) return '';
  // 기본 썸네일 (고해상도가 없을 경우 대비)
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
};

export interface GalleryImage {
  src: string;
  alt: string;
  description?: string;
  type?: 'image' | 'youtube';
}

export interface LessonIdea {
  title: string;
  summary: string;
  duration: string;
  difficulty: '초급' | '중급' | '고급';
}

export interface Mission {
  step: string;
  detail: string;
  successCondition?: string;
}

export interface ResourceLink {
  title: string;
  url: string;
  description?: string;
}

export interface WorldData {
  title: string;
  subtitle: string;
  tags: string[];
  thumbnail: string;
  version: string;
  minecraftVersion: string;
  fileSize: string;
  downloadUrl: string;
  teacherGuideUrl?: string;
  overview: string;
  source?: string;
  learningObjectives: string[];
  recommendedGrade: string;
  playTime: string;
  playerMode: '싱글 플레이' | '멀티 플레이' | '싱글/멀티 지원';
  prerequisites: string[];
  gallery: GalleryImage[];
  installationSteps: string[];
  lessonIdeas: LessonIdea[];
  missions: Mission[];
  tips: string[];
  resourceLinks: ResourceLink[];
}

interface WorldShareTemplateProps {
  worldData?: WorldData;
  onDownload?: () => void;
  mode?: 'view' | 'edit';
  onWorldDataChange?: (data: WorldData) => void;
  onAddGalleryFile?: (file: File) => void;
  onAddGalleryYoutube?: (url: string) => void;
  onRemoveGalleryItem?: (index: number) => void;
  onThumbnailChange?: (file: File) => void;
}

const DEFAULT_WORLD_DATA: WorldData = {
  title: '개인 활동 월드 양식',
  subtitle: '개인별 활동을 원활하게 하기 위한 월드 양식입니다.',
  tags: ['개인 활동', '월드 템플릿', '활동 양식'],
  thumbnail: '/images/minecraft/world-thumbnail-placeholder.png',
  version: 'v1.0',
  minecraftVersion: 'Education Edition 1.20.13',
  fileSize: '45MB',
  downloadUrl: 'https://example.com/your-world.mcworld',
  teacherGuideUrl: 'https://example.com/teacher-guide.pdf',
  overview:
    '이 월드는 학생들이 협력하여 문제를 해결하도록 설계된 학습용 콘텐트입니다. 간단한 배경 설명과 학습 방향성을 요약해서 작성해 주세요.',
  source: '',
  learningObjectives: [
    '학생들은 ○○ 개념을 실습을 통해 이해합니다.',
    '문제 해결 과정을 통해 협업 역량을 기릅니다.',
    '마인크래프트의 기본 조작과 명령어를 익힙니다.'
  ],
  recommendedGrade: '초등 고학년 ~ 중학생',
  playTime: '50분 (2차시 권장)',
  playerMode: '싱글/멀티 지원',
  prerequisites: ['Minecraft Education 설치', '기본 이동/조작 교육', '사전 활동용 워크시트'],
  gallery: [
    {
      src: '/images/minecraft/world-screenshot-1.png',
      alt: '월드 전경 이미지',
      description: '수업 시작 전에 보여줄 월드 메인 화면입니다.',
      type: 'image'
    },
    {
      src: '/images/minecraft/world-screenshot-2.png',
      alt: '미션 지역',
      description: '학생들이 도전할 미션 구역을 소개하세요.',
      type: 'image'
    }
  ],
  installationSteps: [
    '첨부된 .mcworld 파일을 다운로드합니다.',
    '다운로드한 파일을 두 번 클릭하거나 Minecraft Education에 드래그하여 불러옵니다.',
    '월드 이름과 저장 위치를 확인하고 “플레이”를 선택합니다.',
    '수업 전에 교사는 미션 구조와 NPC 대사를 미리 확인하세요.'
  ],
  lessonIdeas: [
    {
      title: '1차시 - 월드 탐험 및 규칙 이해',
      summary: '학생들이 월드를 탐험하며 규칙과 목표를 익힙니다.',
      duration: '50분',
      difficulty: '초급'
    },
    {
      title: '2차시 - 미션 수행 및 협업 활동',
      summary: '조별로 미션을 수행하고 해결 과정을 공유합니다.',
      duration: '50분',
      difficulty: '중급'
    }
  ],
  missions: [
    {
      step: 'STEP 1. 준비 구역에서 아이템 받기',
      detail: 'NPC에게 말을 걸어 미션 북과 기본 도구를 수령합니다.',
      successCondition: '모든 조원이 미션 북을 보유해야 합니다.'
    },
    {
      step: 'STEP 2. 협업 퍼즐 해결',
      detail: '제시된 퍼즐을 제한 시간 안에 해결해 다음 구역으로 이동하세요.',
      successCondition: '각 조의 인증 버튼이 동시에 활성화되어야 합니다.'
    }
  ],
  tips: [
    '멀티 플레이 시에는 호스트 교사가 월드를 실행하고 학생들을 초대하세요.',
    '기기 성능이 낮은 경우 렌더 거리와 그래픽 옵션을 낮추면 원활합니다.',
    '수업 전에 모든 NPC 대사와 명령 블록이 정상 작동하는지 확인하세요.'
  ],
  resourceLinks: [
    {
      title: '교사용 지도안 예시',
      url: 'https://example.com/lesson-plan.pdf',
      description: '수업 흐름과 평가 기준이 포함된 지도안 예시 파일입니다.'
    },
    {
      title: '학생용 워크시트',
      url: 'https://example.com/student-worksheet.pdf'
    }
  ]
};

const cloneWorldData = (data: WorldData): WorldData => ({
  ...data,
  tags: [...data.tags],
  learningObjectives: [...data.learningObjectives],
  prerequisites: [...data.prerequisites],
  installationSteps: [...data.installationSteps],
  tips: [...data.tips],
  gallery: data.gallery.map((item) => ({ ...item })),
  lessonIdeas: data.lessonIdeas.map((idea) => ({ ...idea })),
  missions: data.missions.map((mission) => ({ ...mission })),
  resourceLinks: data.resourceLinks.map((link) => ({ ...link }))
});

const WorldShareTemplate: React.FC<WorldShareTemplateProps> = ({
  worldData: propWorldData,
  onDownload,
  mode = 'view',
  onWorldDataChange,
  onAddGalleryFile,
  onAddGalleryYoutube,
  onRemoveGalleryItem,
  onThumbnailChange
}) => {
  const isEditable = mode === 'edit' && !!onWorldDataChange;
  const galleryFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const thumbnailFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [youtubeInput, setYoutubeInput] = React.useState('');

  const worldData = React.useMemo<WorldData>(() => {
    const base = propWorldData ? { ...DEFAULT_WORLD_DATA, ...propWorldData } : DEFAULT_WORLD_DATA;
    const normalized: WorldData = {
      ...DEFAULT_WORLD_DATA,
      ...base,
      tags: base.tags ?? [],
      learningObjectives: base.learningObjectives ?? [],
      prerequisites: base.prerequisites ?? [],
      installationSteps: base.installationSteps ?? [],
      tips: base.tips ?? [],
      gallery: (base.gallery ?? []).map((item) => ({
        src: item.src ?? '',
        alt: item.alt ?? '',
        description: item.description ?? '',
        type: item.type ?? (isYoutubeSource(item.src ?? '') ? 'youtube' : 'image')
      })),
      lessonIdeas: (base.lessonIdeas ?? []).map((item) => ({
        title: item.title ?? '',
        summary: item.summary ?? '',
        duration: item.duration ?? '',
        difficulty: item.difficulty ?? '초급'
      })),
      missions: (base.missions ?? []).map((item) => ({
        step: item.step ?? '',
        detail: item.detail ?? '',
        successCondition: item.successCondition ?? ''
      })),
      resourceLinks: (base.resourceLinks ?? []).map((item) => ({
        title: item.title ?? '',
        url: item.url ?? '',
        description: item.description ?? ''
      })),
      subtitle: base.subtitle ?? '',
      overview: base.overview ?? '',
      recommendedGrade: base.recommendedGrade ?? '',
      playTime: base.playTime ?? '',
      playerMode: base.playerMode ?? '싱글/멀티 지원',
      version: base.version ?? '',
      minecraftVersion: base.minecraftVersion ?? '',
      fileSize: base.fileSize ?? ''
    };
    return cloneWorldData(normalized);
  }, [propWorldData]);

  const updateData = React.useCallback(
    (mutator: (draft: WorldData) => void) => {
      if (!isEditable || !onWorldDataChange) return;
      const draft = cloneWorldData(worldData);
      mutator(draft);
      onWorldDataChange(draft);
    },
    [isEditable, onWorldDataChange, worldData]
  );

  const handleInputChange = (field: keyof WorldData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateData((draft) => {
      (draft[field] as unknown as string) = event.target.value;
    });
  };

  const handleTextareaChange = (field: keyof WorldData) => (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    updateData((draft) => {
      (draft[field] as unknown as string) = event.target.value;
    });
  };

  const handlePlayerModeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    updateData((draft) => {
      draft.playerMode = event.target.value as WorldData['playerMode'];
    });
  };

  const handleTagChange = (index: number, value: string) => {
    updateData((draft) => {
      const tags = [...draft.tags];
      tags[index] = value;
      draft.tags = tags;
    });
  };

  const addTag = () => {
    updateData((draft) => {
      draft.tags = [...draft.tags, ''];
    });
  };

  const removeTag = (index: number) => {
    updateData((draft) => {
      const tags = [...draft.tags];
      tags.splice(index, 1);
      draft.tags = tags;
    });
  };

  const handleStringListChange = (
    field: 'learningObjectives' | 'prerequisites' | 'installationSteps' | 'tips',
    index: number,
    value: string
  ) => {
    updateData((draft) => {
      const list = [...(draft[field] as string[])];
      list[index] = value;
      (draft[field] as string[]) = list;
    });
  };

  const addStringListItem = (
    field: 'learningObjectives' | 'prerequisites' | 'installationSteps' | 'tips'
  ) => {
    updateData((draft) => {
      (draft[field] as string[]) = [...(draft[field] as string[]), ''];
    });
  };

  const removeStringListItem = (
    field: 'learningObjectives' | 'prerequisites' | 'installationSteps' | 'tips',
    index: number
  ) => {
    updateData((draft) => {
      const list = [...(draft[field] as string[])];
      list.splice(index, 1);
      (draft[field] as string[]) = list;
    });
  };

  const handleLessonIdeaChange = (
    index: number,
    key: keyof LessonIdea,
    value: string
  ) => {
    updateData((draft) => {
      const ideas = draft.lessonIdeas.map((idea, ideaIndex) =>
        ideaIndex === index ? { ...idea, [key]: value } : idea
      );
      draft.lessonIdeas = ideas;
    });
  };

  const addLessonIdea = () => {
    updateData((draft) => {
      draft.lessonIdeas = [
        ...draft.lessonIdeas,
        { title: '', summary: '', duration: '', difficulty: '초급' }
      ];
    });
  };

  const removeLessonIdea = (index: number) => {
    updateData((draft) => {
      const ideas = [...draft.lessonIdeas];
      ideas.splice(index, 1);
      draft.lessonIdeas = ideas;
    });
  };

  const handleMissionChange = (
    index: number,
    key: keyof Mission,
    value: string
  ) => {
    updateData((draft) => {
      const missions = draft.missions.map((mission, missionIndex) =>
        missionIndex === index ? { ...mission, [key]: value } : mission
      );
      draft.missions = missions;
    });
  };

  const addMission = () => {
    updateData((draft) => {
      draft.missions = [
        ...draft.missions,
        { step: '', detail: '', successCondition: '' }
      ];
    });
  };

  const removeMission = (index: number) => {
    updateData((draft) => {
      const missions = [...draft.missions];
      missions.splice(index, 1);
      draft.missions = missions;
    });
  };

  const handleResourceLinkChange = (
    index: number,
    key: keyof ResourceLink,
    value: string
  ) => {
    updateData((draft) => {
      const links = draft.resourceLinks.map((link, linkIndex) =>
        linkIndex === index ? { ...link, [key]: value } : link
      );
      draft.resourceLinks = links;
    });
  };

  const addResourceLink = () => {
    updateData((draft) => {
      draft.resourceLinks = [
        ...draft.resourceLinks,
        { title: '', url: '', description: '' }
      ];
    });
  };

  const removeResourceLink = (index: number) => {
    updateData((draft) => {
      const links = [...draft.resourceLinks];
      links.splice(index, 1);
      draft.resourceLinks = links;
    });
  };

  const handleGalleryMetaChange = (
    index: number,
    key: keyof GalleryImage,
    value: string
  ) => {
    updateData((draft) => {
      const gallery = draft.gallery.map((image, imageIndex) =>
        imageIndex === index ? { ...image, [key]: value } : image
      );
      draft.gallery = gallery;
    });
  };

  // 갤러리 공통 리스트 (이미지+유튜브, 썸네일 fallback 포함)
  const galleryWithFallback = React.useMemo(() => {
    const base =
      worldData.gallery && worldData.gallery.length > 0
        ? worldData.gallery
        : [
            {
              src: worldData.thumbnail,
              alt: `${worldData.title} 썸네일`,
              description: undefined,
              type: 'image' as const
            }
          ];
    // 유튜브 항목을 앞쪽으로 정렬 (영상이 먼저 나오도록)
    const youtubeItems = base.filter(
      (g) =>
        (g.type ?? (isYoutubeSource(g.src) ? 'youtube' : 'image')) === 'youtube'
    );
    const imageItemsOnly = base.filter(
      (g) =>
        (g.type ?? (isYoutubeSource(g.src) ? 'youtube' : 'image')) === 'image'
    );
    return [...youtubeItems, ...imageItemsOnly];
  }, [worldData.gallery, worldData.thumbnail, worldData.title]);

  // 이미지만
  const imageItems = React.useMemo(() => {
    return galleryWithFallback.filter(
      (g) =>
        (g.type ?? (isYoutubeSource(g.src) ? 'youtube' : 'image')) === 'image'
    );
  }, [galleryWithFallback]);

  // 유튜브만
  const videoItems = React.useMemo(() => {
    return galleryWithFallback.filter(
      (g) =>
        (g.type ?? (isYoutubeSource(g.src) ? 'youtube' : 'image')) === 'youtube'
    );
  }, [galleryWithFallback]);

  // 월드 미리보기: 큰 이미지 1장 + 아래 썸네일 줄
  const [activeIndex, setActiveIndex] = React.useState(0);

  const scrollToIndex = (index: number) => {
    setActiveIndex(index);
  };

  const sectionCardStyle: React.CSSProperties = {
    width: '100%',
    padding: '28px 24px',
    gap: '16px'
  };

  const sectionTitleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: '#161616'
  };

  const badgeStyle: React.CSSProperties = {
    padding: '6px 12px',
    backgroundColor: '#F5F5F5',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#4C4C4C'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    border: '1px solid #E0E0E0',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  };

  const headerFieldBaseStyle: React.CSSProperties = {
    width: '100%',
    borderRadius: '8px',
    padding: '10px 16px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    minHeight: '44px',
    backgroundColor: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white'
  };

  const headerSubtitleDisplayStyle: React.CSSProperties = {
    ...headerFieldBaseStyle,
    border: '1px solid transparent',
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    backgroundColor: 'transparent'
  };

  const headerSubtitleInputStyle: React.CSSProperties = {
    ...headerFieldBaseStyle,
    fontSize: '14px'
  };

  const headerTitleDisplayStyle: React.CSSProperties = {
    ...headerFieldBaseStyle,
    border: '1px solid transparent',
    display: 'flex',
    alignItems: 'center',
    fontSize: '32px',
    fontWeight: 700,
    padding: '12px 16px',
    backgroundColor: 'transparent'
  };

  const headerTitleInputStyle: React.CSSProperties = {
    ...headerFieldBaseStyle,
    fontSize: '32px',
    fontWeight: 700,
    padding: '12px 16px'
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    resize: 'vertical',
    minHeight: '90px'
  };

  const chipContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  };

  const editableChipStyle = (isPrimary: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    borderRadius: '16px',
    backgroundColor: isPrimary ? 'rgba(255,255,255,0.16)' : '#F5F5F5',
    color: isPrimary ? 'white' : '#161616'
  });

  const removeButtonStyle: React.CSSProperties = {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#989898',
    fontSize: '16px'
  };

  const renderStringListEditor = (
    field: 'learningObjectives' | 'prerequisites' | 'installationSteps' | 'tips',
    label: string
  ) => (
    <div>
      <span style={{ fontSize: '13px', fontWeight: 500, color: '#161616', marginBottom: '4px', display: 'block' }}>{label}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {(worldData[field] as string[]).map((item, index) => (
          <div key={`${field}-${index}`} style={{ display: 'flex', gap: '8px' }}>
            <input
              value={item}
              onChange={(event) => handleStringListChange(field, index, event.target.value)}
              style={inputStyle}
            />
            <button type="button" onClick={() => removeStringListItem(field, index)} style={removeButtonStyle}>
              ×
            </button>
          </div>
        ))}
        <Button width="120px" height="36px" variant="outline" onClick={() => addStringListItem(field)}>
          항목 추가
        </Button>
      </div>
    </div>
  );

  return (
    <div style={{ width: '100%', height: '100%', background: '#F4F6FA', overflowY: 'auto', overflowX: 'hidden', overscrollBehaviorX: 'none', touchAction: 'pan-y' }}>
      <Card
        width="100%"
        padding="32px 32px"
        alignItems="flex-start"
        gap={24}
        style={{ marginBottom: '24px', background: 'linear-gradient(135deg, #293044 0%, #44506A 100%)', color: 'white' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          {isEditable ? (
            <input
              value={worldData.subtitle}
              onChange={handleInputChange('subtitle')}
              style={headerSubtitleInputStyle}
            />
          ) : (
            <div style={headerSubtitleDisplayStyle}>{worldData.subtitle}</div>
          )}

          {isEditable ? (
            <input
              value={worldData.title}
              onChange={handleInputChange('title')}
              style={headerTitleInputStyle}
            />
          ) : (
            <div style={headerTitleDisplayStyle}>{worldData.title}</div>
          )}

          <div style={chipContainerStyle}>
            {isEditable ? (
              <>
                {worldData.tags.map((tag, index) => (
                  <div key={`tag-${index}`} style={editableChipStyle(true)}>
                    <input
                      value={tag}
                      onChange={(event) => handleTagChange(index, event.target.value)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: 'inherit',
                        fontSize: '12px',
                        outline: 'none'
                      }}
                    />
                    <button type="button" onClick={() => removeTag(index)} style={{ ...removeButtonStyle, color: 'white' }}>
                      ×
                    </button>
                  </div>
                ))}
                <Button width="120px" height="32px" variant="outline" onClick={addTag}>
                  태그 추가
                </Button>
              </>
            ) : (
              worldData.tags.map((tag) => (
                <span key={tag} style={{ ...badgeStyle, backgroundColor: 'rgba(255,255,255,0.16)', color: 'white' }}>{tag}</span>
              ))
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div
            style={{
              boxShadow: '0 4px 12px rgba(255, 111, 15, 0.3)',
              borderRadius: '8px'
            }}
          >
            <Button
              width="180px"
              height="48px"
              variant="primary"
              onClick={() => {
                if (isEditable) return;
                if (onDownload) {
                  onDownload();
                } else {
                  window.open(worldData.downloadUrl, '_blank');
                }
              }}
              disabled={isEditable}
            >
              월드 다운로드
            </Button>
          </div>
          {worldData.teacherGuideUrl && (
            <Button
              width="160px"
              height="44px"
              variant="outline"
              onClick={() => !isEditable && window.open(worldData.teacherGuideUrl!, '_blank')}
              disabled={isEditable}
            >
              교사용 가이드
            </Button>
          )}
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '24px', marginBottom: '24px' }}>
        <Card width="100%" padding="0" alignItems="stretch" gap={0}>
          <div style={{ 
            position: 'relative', 
            width: '100%',
            border: isEditable ? '2px dashed #E0E0E0' : 'none',
            borderRadius: '16px 16px 0 0',
            overflow: 'hidden',
            backgroundColor: '#F5F5F5'
          }}>
            <img
              src={worldData.thumbnail}
              alt={`${worldData.title} 썸네일`}
              style={{ 
                width: '100%', 
                height: '220px', 
                objectFit: 'cover',
                display: 'block'
              }}
            />
            {isEditable && onThumbnailChange && (
              <>
                <input
                  ref={thumbnailFileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(event) => {
                    if (!onThumbnailChange || !event.target.files || event.target.files.length === 0) return;
                    onThumbnailChange(event.target.files[0]);
                    event.target.value = '';
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0';
                }}
                onClick={() => thumbnailFileInputRef.current?.click()}
                >
                  <div style={{
                    padding: '12px 24px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#161616',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}>
                    이미지 변경
                  </div>
                </div>
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  padding: '6px 12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#4C4C4C',
                  border: '1px solid rgba(0, 0, 0, 0.1)'
                }}>
                  편집 가능
                </div>
              </>
            )}
          </div>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'grid', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#4C4C4C' }}>버전</span>
              {isEditable ? (
                <input value={worldData.version} onChange={handleInputChange('version')} style={inputStyle} />
              ) : (
                <strong style={{ fontSize: '16px' }}>{worldData.version}</strong>
              )}
            </div>
            <div style={{ display: 'grid', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#4C4C4C' }}>지원 MC 버전</span>
              {isEditable ? (
                <input value={worldData.minecraftVersion} onChange={handleInputChange('minecraftVersion')} style={inputStyle} />
              ) : (
                <strong style={{ fontSize: '16px' }}>{worldData.minecraftVersion}</strong>
              )}
            </div>
            <div style={{ display: 'grid', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#4C4C4C' }}>파일 용량</span>
              {isEditable ? (
                <input value={worldData.fileSize} onChange={handleInputChange('fileSize')} style={inputStyle} />
              ) : (
                <strong style={{ fontSize: '16px' }}>{worldData.fileSize}</strong>
              )}
            </div>
          </div>
        </Card>

        <Card width="100%" padding="28px 24px" alignItems="flex-start" gap={16}>
          <h2 style={sectionTitleStyle}>월드 소개</h2>
          {isEditable ? (
            <>
              <textarea
                value={worldData.overview}
                onChange={handleTextareaChange('overview')}
                style={textareaStyle}
              />
              <div style={{ marginTop: '8px', width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#65729A', fontWeight: 600 }}>출처</span>
                <input
                  value={worldData.source ?? ''}
                  onChange={handleInputChange('source')}
                  placeholder="예: Microsoft 공식 월드 / MARU 제작"
                  style={inputStyle}
                />
              </div>
            </>
          ) : (
            <>
              <p style={{ margin: 0, lineHeight: 1.6, color: '#4C4C4C' }}>{worldData.overview}</p>
              {worldData.source && (
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#7A7A7A' }}>
                  <strong style={{ fontWeight: 600 }}>출처</strong>:{' '}
                  {/^https?:\/\//.test(worldData.source)
                    ? (
                      <a
                        href={worldData.source}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: '#1a73e8', textDecoration: 'underline' }}
                      >
                        {worldData.source}
                      </a>
                    )
                    : worldData.source}
                </p>
              )}
            </>
          )}
          <div style={{ display: 'grid', gap: '12px' }}>
            {isEditable
              ? renderStringListEditor('learningObjectives', '학습 목표')
              : (
                <div>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#161616', marginBottom: '4px', display: 'block' }}>학습 목표</span>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#4C4C4C' }}>
                    {worldData.learningObjectives.map((objective, index) => (
                      <li key={`objective-${index}`} style={{ marginBottom: '6px' }}>{objective}</li>
                    ))}
                  </ul>
                </div>
              )}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {isEditable ? (
                <>
                  <div style={{ minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#65729A', fontWeight: 600 }}>추천 대상</span>
                    <input value={worldData.recommendedGrade} onChange={handleInputChange('recommendedGrade')} style={inputStyle} />
                  </div>
                  <div style={{ minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#65729A', fontWeight: 600 }}>권장 진행 시간</span>
                    <input value={worldData.playTime} onChange={handleInputChange('playTime')} style={inputStyle} />
                  </div>
                  <div style={{ minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#65729A', fontWeight: 600 }}>플레이 방식</span>
                    <select value={worldData.playerMode} onChange={handlePlayerModeChange} style={inputStyle as React.CSSProperties}>
                      <option value="싱글 플레이">싱글 플레이</option>
                      <option value="멀티 플레이">멀티 플레이</option>
                      <option value="싱글/멀티 지원">싱글/멀티 지원</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <InfoBadge label="추천 대상" value={worldData.recommendedGrade} />
                  <InfoBadge label="권장 진행 시간" value={worldData.playTime} />
                  <InfoBadge label="플레이 방식" value={worldData.playerMode} />
                </>
              )}
            </div>
            {isEditable
              ? renderStringListEditor('prerequisites', '수업 전 준비물')
              : (
                <div>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#161616', marginBottom: '4px', display: 'block' }}>수업 전 준비물</span>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#4C4C4C' }}>
                    {worldData.prerequisites.map((item, index) => (
                      <li key={`prerequisite-${index}`} style={{ marginBottom: '4px' }}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        </Card>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          marginBottom: '32px'
        }}
      >
      <Card style={sectionCardStyle}>
        <h2 style={sectionTitleStyle}>월드 미리보기</h2>
          {(!worldData.gallery || worldData.gallery.length === 0) && (
            <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#65729A' }}>
              갤러리 이미지가 없습니다. 썸네일을 대신 표시합니다.
            </p>
          )}
          {isEditable && (!worldData.gallery || worldData.gallery.length === 0) && (
            <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#989898' }}>
              아래 버튼을 사용해 이미지를 업로드하거나 YouTube 링크를 추가해 보세요.
            </p>
          )}
          {isEditable && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <input
                ref={galleryFileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={(event) => {
                  if (!onAddGalleryFile || !event.target.files) return;
                  Array.from(event.target.files).forEach((file) => {
                    onAddGalleryFile(file);
                  });
                  event.target.value = '';
                }}
              />
              <Button
                width="auto"
                height="36px"
                variant="outline"
                onClick={() => galleryFileInputRef.current?.click()}
              >
                이미지 추가
              </Button>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap' }}>
                <input
                  value={youtubeInput}
                  onChange={(event) => setYoutubeInput(event.target.value)}
                  placeholder="YouTube 링크 입력"
                  style={{
                    ...inputStyle,
                    maxWidth: '280px',
                    minWidth: '200px'
                  }}
                />
                <Button
                  width="auto"
                  height="36px"
                  variant="outline"
                  onClick={() => {
                    const trimmed = youtubeInput.trim();
                    if (!trimmed || !onAddGalleryYoutube) return;
                    onAddGalleryYoutube(trimmed);
                    setYoutubeInput('');
                  }}
                >
                  링크 추가
                </Button>
              </div>
            </div>
          )}
          {galleryWithFallback.length > 0 && (
            <>
              {/* 큰 이미지 1장 (현재 선택된 인덱스) */}
              {(() => {
                const safeIndex = Math.min(
                  Math.max(activeIndex, 0),
                  galleryWithFallback.length - 1
                );
                const item = galleryWithFallback[safeIndex];
                const galleryIndex = worldData.gallery.indexOf(item);
                const isYoutube =
                  (item.type ??
                    (isYoutubeSource(item.src) ? 'youtube' : 'image')) ===
                  'youtube';
                return (
                  <div
                    style={{
                      width: '100%',
                      maxWidth: '960px',
                      borderRadius: '14px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                      marginBottom: '12px'
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        maxWidth: '960px',
                        height: '540px',
                        position: 'relative'
                      }}
                    >
                      {isYoutube ? (
                        <iframe
                          src={toYoutubeEmbedUrl(item.src)}
                          title={item.alt || `월드 영상 ${safeIndex + 1}`}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            display: 'block'
                          }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <img
                          src={item.src}
                          alt={item.alt}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                      )}
                    </div>
                    {isEditable ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          padding: '12px 12px 16px'
                        }}
                      >
                        <input
                          value={item.alt}
                          onChange={(event) =>
                            handleGalleryMetaChange(
                              galleryIndex,
                              'alt',
                              event.target.value
                            )
                          }
                          style={inputStyle}
                        />
                        <textarea
                          value={item.description ?? ''}
                          onChange={(event) =>
                            handleGalleryMetaChange(
                              galleryIndex,
                              'description',
                              event.target.value
                            )
                          }
                          style={textareaStyle}
                        />
                        {onRemoveGalleryItem && (
                          <button
                            type="button"
                            onClick={() => onRemoveGalleryItem(galleryIndex)}
                            style={{
                              alignSelf: 'flex-end',
                              padding: '4px 10px',
                              border: '1px solid #E0E0E0',
                              borderRadius: '8px',
                              background: '#FFFFFF',
                              cursor: 'pointer',
                              fontSize: '12px',
                              color: '#FF6F0F'
                            }}
                          >
                            삭제
                          </button>
                        )}
                      </div>
                    ) : (
                      <div style={{ padding: '8px 12px 12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 500 }}>
                          {item.alt}
                        </span>
                        {item.description && (
                          <p
                            style={{
                              margin: 0,
                              fontSize: '12px',
                              color: '#4C4C4C',
                              lineHeight: 1.5
                            }}
                          >
                            {item.description}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
          {galleryWithFallback.length > 1 && (
            <div
              style={{
                marginTop: '12px',
                display: 'flex',
                gap: '8px',
                overflowX: 'auto',
                paddingBottom: '4px'
              }}
              aria-label="이미지 썸네일 목록"
              role="tablist"
            >
              {galleryWithFallback.map((img, tIndex) => {
                const isYoutubeThumb =
                  (img.type ??
                    (isYoutubeSource(img.src) ? 'youtube' : 'image')) ===
                  'youtube';
                const thumbSrc = isYoutubeThumb
                  ? getYoutubeThumbnailUrl(img.src) || img.src
                  : img.src;
                return (
                <button
                  key={`thumb-${tIndex}`}
                  type="button"
                  onClick={() => scrollToIndex(tIndex)}
                  role="tab"
                  aria-selected={tIndex === activeIndex}
                  aria-label={`${tIndex + 1}번째 이미지 보기`}
                  style={{
                    padding: 0,
                    border:
                      tIndex === activeIndex
                        ? '2px solid #65729A'
                        : '1px solid #E0E0E0',
                    borderRadius: '8px',
                    width: '80px',
                    height: '52px',
                    overflow: 'hidden',
                    background: '#FFF',
                    cursor: 'pointer',
                    flex: '0 0 auto'
                  }}
                >
                  <img
                    src={thumbSrc}
                    alt={img.alt || `미리보기 ${tIndex + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                </button>
              );})}
            </div>
          )}
      </Card>

      <Card style={sectionCardStyle}>
        <h2 style={sectionTitleStyle}>설치 방법</h2>
        {isEditable ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
            {worldData.installationSteps.map((step, index) => (
              <div key={`install-${index}`} style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                <input
                  value={step}
                  onChange={(event) => handleStringListChange('installationSteps', index, event.target.value)}
                  style={{ 
                    ...inputStyle, 
                    width: '100%',
                    flex: 1,
                    minWidth: 0
                  }}
                />
                <button type="button" onClick={() => removeStringListItem('installationSteps', index)} style={{ ...removeButtonStyle, flexShrink: 0 }}>
                  ×
                </button>
              </div>
            ))}
            <Button width="120px" height="36px" variant="outline" onClick={() => addStringListItem('installationSteps')}>
              항목 추가
            </Button>
          </div>
        ) : (
          <ol style={{ margin: 0, paddingLeft: '20px', color: '#4C4C4C', lineHeight: 1.6 }}>
            {worldData.installationSteps.map((step, index) => (
              <li key={`install-${index}`} style={{ marginBottom: '8px' }}>{step}</li>
            ))}
          </ol>
        )}
      </Card>

      <Card style={sectionCardStyle}>
        <h2 style={sectionTitleStyle}>수업 아이디어</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {worldData.lessonIdeas.map((idea, index) => (
            <div key={`idea-${index}`} style={{
              border: '1px solid #F0F0F0',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              background: '#FAFBFF'
            }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#65729A' }}>{idea.difficulty} • {idea.duration}</span>
              {isEditable ? (
                <>
                  <input
                    value={idea.title}
                    onChange={(event) => handleLessonIdeaChange(index, 'title', event.target.value)}
                    style={inputStyle}
                  />
                  <textarea
                    value={idea.summary}
                    onChange={(event) => handleLessonIdeaChange(index, 'summary', event.target.value)}
                    style={textareaStyle}
                  />
                  <input
                    value={idea.duration}
                    onChange={(event) => handleLessonIdeaChange(index, 'duration', event.target.value)}
                    style={inputStyle}
                  />
                  <select
                    value={idea.difficulty}
                    onChange={(event) => handleLessonIdeaChange(index, 'difficulty', event.target.value as LessonIdea['difficulty'])}
                    style={inputStyle as React.CSSProperties}
                  >
                    <option value="초급">초급</option>
                    <option value="중급">중급</option>
                    <option value="고급">고급</option>
                  </select>
                  <button type="button" onClick={() => removeLessonIdea(index)} style={removeButtonStyle}>
                    삭제
                  </button>
                </>
              ) : (
                <>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{idea.title}</h3>
                  <p style={{ margin: 0, fontSize: '13px', color: '#4C4C4C', lineHeight: 1.5 }}>{idea.summary}</p>
                  <span style={{ fontSize: '12px', color: '#65729A' }}>{idea.duration}</span>
                  <span style={{ fontSize: '12px', color: '#65729A' }}>{idea.difficulty}</span>
                </>
              )}
            </div>
          ))}
          {isEditable && (
            <Button width="100%" height="44px" variant="outline" onClick={addLessonIdea}>
              수업 아이디어 추가
            </Button>
          )}
        </div>
      </Card>

      <Card style={sectionCardStyle}>
        <h2 style={sectionTitleStyle}>미션 구성</h2>
        <div style={{ display: 'grid', gap: '12px' }}>
          {worldData.missions.map((mission, index) => (
            <div key={`mission-${index}`} style={{
              border: '1px solid #F0F0F0',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}>
              {isEditable ? (
                <>
                  <input
                    value={mission.step}
                    onChange={(event) => handleMissionChange(index, 'step', event.target.value)}
                    style={inputStyle}
                  />
                  <textarea
                    value={mission.detail}
                    onChange={(event) => handleMissionChange(index, 'detail', event.target.value)}
                    style={textareaStyle}
                  />
                  <input
                    value={mission.successCondition ?? ''}
                    onChange={(event) => handleMissionChange(index, 'successCondition', event.target.value)}
                    style={inputStyle}
                  />
                  <button type="button" onClick={() => removeMission(index)} style={removeButtonStyle}>
                    삭제
                  </button>
                </>
              ) : (
                <>
                  <strong style={{ fontSize: '15px', color: '#293044' }}>{mission.step}</strong>
                  <span style={{ fontSize: '13px', color: '#4C4C4C' }}>{mission.detail}</span>
                  {mission.successCondition && (
                    <span style={{ fontSize: '12px', color: '#989898' }}>✔ 성공 조건: {mission.successCondition}</span>
                  )}
                </>
              )}
            </div>
          ))}
          {isEditable && (
            <Button width="100%" height="44px" variant="outline" onClick={addMission}>
              미션 추가
            </Button>
          )}
        </div>
      </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <Card style={sectionCardStyle}>
          <h2 style={sectionTitleStyle}>진행 팁</h2>
          {isEditable ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {worldData.tips.map((tip, index) => (
                <div key={`tip-${index}`} style={{ display: 'flex', gap: '8px' }}>
                  <input
                    value={tip}
                    onChange={(event) => handleStringListChange('tips', index, event.target.value)}
                    style={inputStyle}
                  />
                  <button type="button" onClick={() => removeStringListItem('tips', index)} style={removeButtonStyle}>
                    ×
                  </button>
                </div>
              ))}
              <Button width="100%" height="44px" variant="outline" onClick={() => addStringListItem('tips')}>
                팁 추가
              </Button>
            </div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#4C4C4C', lineHeight: 1.6 }}>
              {worldData.tips.map((tip, index) => (
                <li key={`tip-${index}`} style={{ marginBottom: '8px' }}>{tip}</li>
              ))}
            </ul>
          )}
        </Card>
        <Card style={sectionCardStyle}>
          <h2 style={sectionTitleStyle}>추가 자료</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {worldData.resourceLinks.map((resource, index) => (
              <div key={`resource-${index}`} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '12px 16px',
                borderRadius: '12px',
                background: '#FFFFFF',
                border: '1px solid #F0F0F0',
                textDecoration: 'none',
                color: '#161616'
              }}>
                {isEditable ? (
                  <>
                    <input
                      value={resource.title}
                      onChange={(event) => handleResourceLinkChange(index, 'title', event.target.value)}
                      style={inputStyle}
                    />
                    <input
                      value={resource.url}
                      onChange={(event) => handleResourceLinkChange(index, 'url', event.target.value)}
                      style={inputStyle}
                    />
                    <textarea
                      value={resource.description ?? ''}
                      onChange={(event) => handleResourceLinkChange(index, 'description', event.target.value)}
                      style={textareaStyle}
                    />
                    <button type="button" onClick={() => removeResourceLink(index)} style={removeButtonStyle}>
                      삭제
                    </button>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>{resource.title}</span>
                    {resource.description && (
                      <span style={{ fontSize: '12px', color: '#4C4C4C' }}>{resource.description}</span>
                    )}
                    <span style={{ fontSize: '12px', color: '#65729A' }}>링크 바로가기 →</span>
                  </>
                )}
              </div>
            ))}
            {isEditable && (
              <Button width="100%" height="44px" variant="outline" onClick={addResourceLink}>
                자료 추가
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

interface InfoBadgeProps {
  label: string;
  value: string;
}

const InfoBadge: React.FC<InfoBadgeProps> = ({ label, value }) => {
  return (
    <div style={{
      padding: '12px 16px',
      borderRadius: '12px',
      background: '#FAFBFF',
      border: '1px solid #E3E9FF',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      minWidth: '160px'
    }}>
      <span style={{ fontSize: '12px', color: '#65729A', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: '14px', color: '#161616', fontWeight: 600 }}>{value}</span>
    </div>
  );
};

export default WorldShareTemplate;

